import React from "react";


const EFFECTIVE_DATE = "October 11, 2025";

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl shadow-sm p-6">
    {children}
  </div>
);

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto mt-20 mb-20 p-6 sm:p-8">
      <h1 className="text-3xl font-semibold mb-4">Privacy Policy</h1>
      <p className="text-sm text-gray-600 mb-6">
        Effective date: <strong>{EFFECTIVE_DATE}</strong>
      </p>

      <Card>
        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            Welcome to <strong>Fleeby</strong> (accessible at{" "}
            <a href="https://www.fleeby.in" className="underline">
              https://www.fleeby.in
            </a>
            ), an e-commerce website based in India. This Privacy Policy
            describes how we collect, use, and protect your personal information
            when you visit or make a purchase from our site. By using our
            website, you agree to this policy.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Information we collect</h2>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-2">
            <li>
              <strong>Account details:</strong> When you create an account or
              log in, we collect your name, email address, and profile image.
              These are stored securely and used only for identification and
              profile display.
            </li>
            <li>
              <strong>Order information:</strong> When you make a purchase, we
              collect necessary details such as your shipping address, phone
              number, and payment confirmation through Razorpay. We do not store
              full payment information such as card or UPI details.
            </li>
            <li>
              <strong>Custom product communication:</strong> For custom resin
              art orders via WhatsApp, we may collect chat content and payment
              proof shared voluntarily by you to confirm and complete your
              order.
            </li>
            <li>
              <strong>Analytics data:</strong> We use Google Analytics to
              understand website traffic and usage patterns. This may collect
              anonymized data such as browser type, time spent, pages visited,
              and approximate location.
            </li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            How we use your information
          </h2>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-2">
            <li>To create and manage your account on Fleeby.</li>
            <li>
              To process and deliver your orders, including sending status
              updates.
            </li>
            <li>
              To communicate with you about custom resin art requests, order
              status, and support queries.
            </li>
            <li>
              To improve our website experience and offerings based on
              aggregated analytics data.
            </li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            Data storage and security
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We store your account and order information securely on our backend
            servers. Passwords are encrypted and never stored in plain text. We
            do not share, sell, or rent any personal data to third parties.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            We use standard industry security measures to protect your data, but
            no method of transmission or storage is 100% secure. You are
            responsible for maintaining the confidentiality of your login
            credentials.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Payment information</h2>
          <p className="text-gray-700 leading-relaxed">
            Online payments are processed securely through{" "}
            <strong>Razorpay</strong>. We do not store or have access to your
            full card, UPI, or banking information. Razorpay’s own privacy
            policy governs how your payment data is handled.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            For UPI transactions made via WhatsApp for custom resin art, we only
            store basic payment confirmation details necessary to verify your
            payment and link it to your order.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Cookies and tracking</h2>
          <p className="text-gray-700 leading-relaxed">
            Fleeby currently does not use cookies for tracking or targeted
            advertising. Only minimal session information (like login state) is
            maintained locally in your browser to keep you logged in. We do not
            use cookies to store sensitive data or track behavior.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Data retention</h2>
          <p className="text-gray-700 leading-relaxed">
            We retain your basic user and order information for as long as your
            account is active or as required by law for record-keeping. You can
            request deletion of your account and associated data by contacting
            us at{" "}
            <a href="mailto:info@fleeby.in" className="underline">
              info@fleeby.in
            </a>
            .
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Third-party services</h2>
          <p className="text-gray-700 leading-relaxed">
            We rely on trusted third-party services like Razorpay (payments) and
            Google Analytics (analytics). These providers may collect data as
            per their own privacy policies. We encourage you to review their
            respective policies for more details:
          </p>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-1">
            <li>
              <a
                rel="noopener"
                href="https://razorpay.com/privacy/"
                target="_blank"
                className="underline"
              >
                Razorpay Privacy Policy
              </a>
            </li>
            <li>
              <a
                rel="noopener"
                href="https://policies.google.com/privacy"
                target="_blank"
                className="underline"
              >
                Google Privacy Policy
              </a>
            </li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Your rights</h2>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-2">
            <li>
              You can access, correct, or delete your account information at any
              time from your profile settings.
            </li>
            <li>
              You can request data deletion by emailing us at{" "}
              <a href="mailto:info@fleeby.in" className="underline">
                info@fleeby.in
              </a>
              .
            </li>
            <li>
              You can opt out of analytics tracking using browser privacy
              settings or ad blockers.
            </li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Children’s privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            Fleeby does not knowingly collect personal information from children
            under 13. If we learn that information has been collected
            inadvertently, we will delete it immediately upon request.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium mb-2">Contact us</h2>
          <p className="text-gray-700 mb-2">
            For questions or concerns regarding this Privacy Policy:
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                Email:{" "}
                <a href="mailto:info@fleeby.in" className="underline">
                  info@fleeby.in
                </a>
              </p>
              <p className="text-sm text-gray-600">
                Phone/Support:{" "}
                <a href="tel:+917778461609" className="underline">
                  +91 97784 61609
                </a>
              </p>
            </div>
            <div className="text-sm text-gray-500">
              We respond quickly — expect a reply within 1–2 business days.
            </div>
          </div>
        </section>
      </Card>

      <p className="text-xs text-gray-500 mt-4">
        Policy updates: This Privacy Policy may be revised periodically. The
        “Effective date” above indicates the last update.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
